import type jsPDF from 'jspdf';

// ─── Types ───────────────────────────────────────────────────
export interface RichPdfStyle {
  /** Base font size for paragraphs (mm-based pt) */
  baseFontSize?: number;
  /** Heading sizes: [h1, h2, h3] */
  headingSizes?: [number, number, number];
  text: [number, number, number];
  primary: [number, number, number];
  /** Vertical line height multiplier */
  lineHeight?: number;
  /** Spacing after a paragraph */
  paragraphSpacing?: number;
}

export interface RichPdfCtx {
  doc: jsPDF;
  y: number;
  margin: number;
  contentWidth: number;
  pageHeight: number;
  ensureSpace: (needed: number) => void;
}

interface InlineRun {
  text: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
}

interface Block {
  kind: 'paragraph' | 'heading' | 'list-item' | 'hr';
  level?: 1 | 2 | 3;
  ordered?: boolean;
  index?: number; // for ordered list items
  align?: 'left' | 'center' | 'right';
  runs: InlineRun[];
}

// ─── HTML parser (DOM-based, browser only) ───────────────────
function parseHtmlToBlocks(html: string): Block[] {
  const blocks: Block[] = [];

  // Plain text fallback (no HTML tags)
  if (!/<\/?[a-z][\s\S]*>/i.test(html)) {
    html
      .split(/\n+/)
      .filter((l) => l.trim())
      .forEach((line) => {
        blocks.push({
          kind: 'paragraph',
          runs: [{ text: line, bold: false, italic: false, underline: false }],
        });
      });
    return blocks;
  }

  if (typeof document === 'undefined') {
    // SSR / no DOM — strip tags
    blocks.push({
      kind: 'paragraph',
      runs: [{ text: html.replace(/<[^>]+>/g, ''), bold: false, italic: false, underline: false }],
    });
    return blocks;
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  const collectRuns = (
    node: Node,
    style: { bold: boolean; italic: boolean; underline: boolean },
    out: InlineRun[],
  ) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (text) out.push({ text, ...style });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    const next = { ...style };
    if (tag === 'strong' || tag === 'b') next.bold = true;
    if (tag === 'em' || tag === 'i') next.italic = true;
    if (tag === 'u') next.underline = true;
    if (tag === 'br') {
      out.push({ text: '\n', ...style });
      return;
    }
    el.childNodes.forEach((c) => collectRuns(c, next, out));
  };

  const getAlign = (el: HTMLElement): Block['align'] => {
    const ta = (el.style?.textAlign || '').toLowerCase();
    if (ta === 'center' || ta === 'right' || ta === 'left') return ta;
    return 'left';
  };

  const walk = (node: Node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      if (node.nodeType === Node.TEXT_NODE && (node.textContent || '').trim()) {
        blocks.push({
          kind: 'paragraph',
          runs: [{ text: node.textContent || '', bold: false, italic: false, underline: false }],
        });
      }
      return;
    }
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === 'p') {
      const runs: InlineRun[] = [];
      collectRuns(el, { bold: false, italic: false, underline: false }, runs);
      if (runs.length === 0 || runs.every((r) => !r.text.trim())) {
        // Empty paragraph → blank line
        blocks.push({ kind: 'paragraph', runs: [{ text: '', bold: false, italic: false, underline: false }] });
      } else {
        blocks.push({ kind: 'paragraph', align: getAlign(el), runs });
      }
      return;
    }

    if (/^h[1-3]$/.test(tag)) {
      const runs: InlineRun[] = [];
      collectRuns(el, { bold: true, italic: false, underline: false }, runs);
      blocks.push({
        kind: 'heading',
        level: Number(tag[1]) as 1 | 2 | 3,
        align: getAlign(el),
        runs,
      });
      return;
    }

    if (tag === 'hr') {
      blocks.push({ kind: 'hr', runs: [] });
      return;
    }

    if (tag === 'ul' || tag === 'ol') {
      const ordered = tag === 'ol';
      let idx = 1;
      el.querySelectorAll(':scope > li').forEach((li) => {
        const runs: InlineRun[] = [];
        collectRuns(li, { bold: false, italic: false, underline: false }, runs);
        blocks.push({
          kind: 'list-item',
          ordered,
          index: idx++,
          runs,
        });
      });
      return;
    }

    // Unknown wrapper — recurse
    el.childNodes.forEach(walk);
  };

  container.childNodes.forEach(walk);
  return blocks;
}

// ─── Rendering ───────────────────────────────────────────────
function fontStyleFor(run: InlineRun): 'normal' | 'bold' | 'italic' | 'bolditalic' {
  if (run.bold && run.italic) return 'bolditalic';
  if (run.bold) return 'bold';
  if (run.italic) return 'italic';
  return 'normal';
}

function flattenRunsToTokens(runs: InlineRun[]): Array<{ word: string; run: InlineRun }> {
  const tokens: Array<{ word: string; run: InlineRun }> = [];
  for (const run of runs) {
    const parts = run.text.split(/(\n)/);
    for (const part of parts) {
      if (part === '') continue;
      if (part === '\n') {
        tokens.push({ word: '\n', run });
        continue;
      }
      const words = part.split(/(\s+)/);
      for (const w of words) {
        if (w === '') continue;
        tokens.push({ word: w, run });
      }
    }
  }
  return tokens;
}

interface DrawLineOpts {
  align: 'left' | 'center' | 'right';
  indent: number;
  maxWidth: number;
  lineHeight: number;
}

function renderRuns(
  ctx: RichPdfCtx,
  runs: InlineRun[],
  fontSize: number,
  style: RichPdfStyle,
  opts: DrawLineOpts,
) {
  const { doc } = ctx;
  doc.setFontSize(fontSize);
  doc.setTextColor(...style.text);

  const tokens = flattenRunsToTokens(runs);
  let line: Array<{ word: string; run: InlineRun; width: number }> = [];
  let lineWidth = 0;

  const flushLine = (forceBreak = false) => {
    if (line.length === 0 && !forceBreak) return;
    ctx.ensureSpace(opts.lineHeight);
    let cursorX = ctx.margin + opts.indent;
    if (opts.align === 'center') {
      cursorX = ctx.margin + opts.indent + (opts.maxWidth - lineWidth) / 2;
    } else if (opts.align === 'right') {
      cursorX = ctx.margin + opts.indent + (opts.maxWidth - lineWidth);
    }
    for (const tok of line) {
      doc.setFont('helvetica', fontStyleFor(tok.run));
      doc.text(tok.word, cursorX, ctx.y);
      if (tok.run.underline && tok.word.trim()) {
        const w = tok.width;
        doc.setDrawColor(...style.text);
        doc.setLineWidth(0.2);
        doc.line(cursorX, ctx.y + 0.6, cursorX + w, ctx.y + 0.6);
      }
      cursorX += tok.width;
    }
    ctx.y += opts.lineHeight;
    line = [];
    lineWidth = 0;
  };

  for (const tok of tokens) {
    if (tok.word === '\n') {
      flushLine(true);
      continue;
    }
    doc.setFont('helvetica', fontStyleFor(tok.run));
    const w = doc.getTextWidth(tok.word);
    const isWhitespace = /^\s+$/.test(tok.word);
    if (lineWidth + w > opts.maxWidth && line.length > 0) {
      // Wrap — drop trailing whitespace from current line
      while (line.length > 0 && /^\s+$/.test(line[line.length - 1].word)) {
        lineWidth -= line[line.length - 1].width;
        line.pop();
      }
      flushLine();
      if (isWhitespace) continue; // skip leading whitespace on new line
    }
    if (isWhitespace && line.length === 0) continue;
    line.push({ word: tok.word, run: tok.run, width: w });
    lineWidth += w;
  }
  flushLine();
}

/**
 * Draws rich HTML content into the PDF at the current ctx.y position.
 * Supports: <p>, <h1>-<h3>, <strong>/<b>, <em>/<i>, <u>, <ul>/<ol>/<li>, <hr>,
 * <br>, text-align styles. Falls back gracefully on plain text.
 */
export function drawRichHtml(ctx: RichPdfCtx, html: string, style: RichPdfStyle) {
  if (!html || !html.trim()) return;
  const blocks = parseHtmlToBlocks(html);
  const baseSize = style.baseFontSize ?? 9.5;
  const headingSizes = style.headingSizes ?? [16, 13, 11];
  const lh = (size: number) => size * (style.lineHeight ?? 0.45);
  const paraSpacing = style.paragraphSpacing ?? 2.5;
  const indent = 4;

  for (const block of blocks) {
    if (block.kind === 'hr') {
      ctx.ensureSpace(4);
      ctx.doc.setDrawColor(...style.primary);
      ctx.doc.setLineWidth(0.3);
      ctx.doc.line(ctx.margin + indent, ctx.y, ctx.margin + ctx.contentWidth - indent, ctx.y);
      ctx.y += 3;
      continue;
    }

    if (block.kind === 'heading') {
      const size = headingSizes[(block.level || 1) - 1];
      ctx.y += 1;
      // Headings always bold
      const boldRuns = block.runs.map((r) => ({ ...r, bold: true }));
      renderRuns(ctx, boldRuns, size, style, {
        align: block.align || 'left',
        indent,
        maxWidth: ctx.contentWidth - indent * 2,
        lineHeight: lh(size),
      });
      ctx.y += paraSpacing;
      continue;
    }

    if (block.kind === 'list-item') {
      const bullet = block.ordered ? `${block.index}.` : '•';
      ctx.doc.setFontSize(baseSize);
      ctx.doc.setFont('helvetica', 'normal');
      const bulletWidth = ctx.doc.getTextWidth(bullet + ' ');
      ctx.ensureSpace(lh(baseSize));
      ctx.doc.setTextColor(...style.text);
      ctx.doc.text(bullet, ctx.margin + indent, ctx.y);
      const savedY = ctx.y;
      // Render list-item text indented
      renderRuns(ctx, block.runs, baseSize, style, {
        align: 'left',
        indent: indent + bulletWidth + 1,
        maxWidth: ctx.contentWidth - indent * 2 - bulletWidth - 1,
        lineHeight: lh(baseSize),
      });
      // Ensure cursor advanced at least one line
      if (ctx.y === savedY) ctx.y += lh(baseSize);
      continue;
    }

    // Paragraph
    if (block.runs.every((r) => !r.text.trim())) {
      ctx.y += lh(baseSize);
      continue;
    }
    renderRuns(ctx, block.runs, baseSize, style, {
      align: block.align || 'left',
      indent,
      maxWidth: ctx.contentWidth - indent * 2,
      lineHeight: lh(baseSize),
    });
    ctx.y += paraSpacing;
  }
}

/**
 * Returns plain text from HTML (used for variable substitution and fallbacks).
 */
export function htmlToPlainText(html: string): string {
  if (!html) return '';
  if (!/<\/?[a-z][\s\S]*>/i.test(html)) return html;
  if (typeof document === 'undefined') {
    return html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<[^>]+>/g, '');
  }
  const div = document.createElement('div');
  div.innerHTML = html;
  // Convert <br> and block ends to newlines
  div.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  div.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li').forEach((el) => {
    el.append('\n');
  });
  return (div.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
}
